import React, { useState, useRef, useEffect } from 'react';
import { clone } from 'ramda';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  DataGrid,
  PopupMenu,
  Drawer,
  Button,
  TextInputWithLabel
} from 'quantux-ui';
import { blue, gray, salmon } from '../../theme';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import { BASE_URL, DRAWER_BREAK_POINT } from '../../utils';

const DOCS_PER_PAGE = 50;
const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};
const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};
const headerDefs = [
  // Section
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Section',
    field: 'section',
    key: 'section',
    isCustomFilter: false,
    showFilterHint: false,
    filterHint: '',
    filter: true,
    filterType: 'startsWith',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'startsWith', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Sub section
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Sub Section',
    field: 'subSection',
    key: 'subSection',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'startsWith',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Code
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 1,
    enableCellClick: true,
    text: 'CPT Code',
    field: 'code',
    key: 'code',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'startsWith',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'endsWith', 'advanced'],
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Colloquial Description
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 1,
    enableCellClick: true,
    text: 'Colloquial Description',
    field: 'colloquialDescription',
    key: 'colloquialDescription',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 250, minWidth: 250, maxWidth: 250, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Short Description
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 1,
    enableCellClick: true,
    text: 'Short Description',
    field: 'shortDescription',
    key: 'shortDescription',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 250, minWidth: 250, maxWidth: 250, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  },
  // Long Description
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 1,
    enableCellClick: true,
    text: 'Long Description',
    field: 'longDescription',
    key: 'longDescription',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 400, minWidth: 400, maxWidth: 400, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  }
];
const CptDataGrid = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [gridHeaderDefs, setGridHeaderDefs] = useState(headerDefs);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 550);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 150);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [isEditDrawerOpen, toggleEditDrawer] = useState(false);
  const [currentSection, setCurrentSection] = useState('');
  const [currentSubSection, setCurrentSubSection] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [currentColloquialDescription, setCurrentColloquialDescription] = useState('');
  const [currentShortDescription, setCurrentShortDescription] = useState('');
  const [currentLongDescription, setCurrentLongDescription] = useState('');

  const gridParentRef = useRef();
  const gridEllipsisMenu = useRef();

  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      // maxWidth: '972px', // was 914px
      // width: '100%',
      // width: '550px',
      width: `${gridWidth}px`,
      overflow: 'hidden'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '52px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    },
    drawerContainer: {
      background: gray[500],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    textArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    }
  });

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.98 * gridParentRefWidth);
      // const tmp = window.innerWidth - 16;
      console.log(`GridWidth = ${tmp}`);
      setGridWidth(Math.min(tmp, 1304)); // 983
      setGridHeight(window.innerHeight - 150);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  const getData = async () => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    formData.append('filters', JSON.stringify(filters));
    formData.append('sorting', JSON.stringify(sorting));

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/cpt/getCpt`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/cpt/getCptCount`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting users grid data and count');
        console.log(err);
      });

    if (response) {
      const cptDataResponse = await response[0].json();
      const cptCountResponse = await response[1].json();

      if (cptDataResponse && cptDataResponse.success) {
        const data = [];
        for (let i = 0; i < cptDataResponse.cptCodes.length; i++) {
          const activeRecord = cptDataResponse.cptCodes[i];
          data.push({
            id: activeRecord._id,
            section: activeRecord.section,
            subSection: activeRecord.subSection,
            code: activeRecord.code,
            shortDescription: activeRecord.shortDescription,
            longDescription: activeRecord.longDescription,
            colloquialDescription: activeRecord.colloquialDescription
          });
        }
        // setGridData(cptDataResponse.cptCodes);
        setGridData(data);
        setShowLoader(false);
      }

      if (cptCountResponse && cptCountResponse.success) {
        console.log(`[Debug] Total users count = ${cptCountResponse.cptCount}`);
        setDocsCount(cptCountResponse.cptCount);
      }
    }
  };

  // Effects
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Apply filter effects hook
  useEffect(() => {
    console.log('[Debug] filters hook');
    getData();
  }, [filters, currentPage, sorting]);

  // #region Data Grid Event Handlers

  const handleOnClickResetFilters = () => {
    setFilters({});
  };

  const handleFilterTypeUpdate = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    if (updatedFilters[field]) {
      updatedFilters[field].filterType = value;
      updatedFilters[field].value = value !== 'dateRange' ? '' : ':';
    }
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filterType = value;
        el.filter = true;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    setFilters(updatedFilters);
  };

  const handleOnApplyFilter = (dataType, filterType, field, filterValue) => {
    const updatedFilters = clone(filters);
    if (filterValue !== '' && filterValue !== ':') {
      updatedFilters[field] = {
        dataType,
        filterType,
        value: filterValue
      };
    } else {
      delete updatedFilters[field];
    }

    setFilters(updatedFilters);
  };

  const handleOnFilterToggle = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filter = value;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    if (updatedFilters[field]) {
      delete updatedFilters[field];
    }
    setFilters(updatedFilters);
  };

  const handleOnPinChange = (field, pinValue) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.pin = pinValue;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
  };

  const handleOnLoadPage = (page) => {
    console.log('Loading page... ');
    setCurrentPage(page);
  };

  const handleOnSortOrderChanged = (field) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.sortOrder = el.sortOrder === 0
          ? el.sortOrder + 1
          : el.sortOrder + (-el.sortOrder * 2);
      } else {
        el.sortOrder = 0;
      }
    });

    const sort = {};
    for (let i = 0; i < updatedGridHeaderDefs.length; i++) {
      if (updatedGridHeaderDefs[i].sortOrder === 0) {
        continue;
      } else {
        if (updatedGridHeaderDefs[i].field === 'name') {
          // sort['lastName'] = updatedGridHeaderDefs[i].sortOrder;
          sort.lastName = updatedGridHeaderDefs[i].sortOrder;
        } else if (updatedGridHeaderDefs[i].field === 'lastUpdate') {
          // sort['createdOn'] = updatedGridHeaderDefs[i].sortOrder;
          sort.createdOn = updatedGridHeaderDefs[i].sortOrder;
        } else {
          sort[updatedGridHeaderDefs[i].field] = updatedGridHeaderDefs[i].sortOrder;
        }
      }
    }
    setGridHeaderDefs(updatedGridHeaderDefs);
    setSorting(sort);
  };

  const handleOnPopupMenu = (e, idx) => {
    // console.log(e.pageX, e.pageY, idx);
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickShowDetailsMenuItem = () => {
    toggleEditDrawer(true);
    const activeRecord = clone(gridData[popupMenuIndex]);
    setCurrentSection(activeRecord.section);
    setCurrentSubSection(activeRecord.subSection);
    setCurrentCode(activeRecord.code);
    setCurrentColloquialDescription(activeRecord.colloquialDescription);
    setCurrentShortDescription(activeRecord.shortDescription);
    setCurrentLongDescription(activeRecord.longDescription);
  };

  const handleOnClickDeleteMenuItem = () => {};

  const handleOnEditSection = (e) => {
    const { value } = e.target;
    setCurrentSection(value);
  };

  const handleOnEditSubSection = (e) => {
    const { value } = e.target;
    setCurrentSection(value);
  };

  const handleOnEditCptCode = (e) => {
    const { value } = e.target;
    setCurrentCode(value);
  };

  const handleOnEditColloquialDescription = (e) => {
    const { value } = e.target;
    setCurrentColloquialDescription(value);
  };

  const handleOnEditShortDescription = (e) => {
    const { value } = e.target;
    setCurrentShortDescription(value);
  };

  const handleOnEditLongDescription = (e) => {
    const { value } = e.target;
    setCurrentLongDescription(value);
  };

  const handleOnClickSaveRecord = async () => {
    const formData = new FormData();
    formData.append('_id', gridData[popupMenuIndex].id);
    formData.append('section', currentSection);
    formData.append('subSection', currentSubSection);
    formData.append('code', currentCode);
    formData.append('colloquialDescription', currentColloquialDescription);
    formData.append('shortDescription', currentShortDescription);
    formData.append('longDescription', currentLongDescription);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cpt/saveCpt`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        if (reqData.success) {
          const updatedGridData = clone(gridData);
          updatedGridData[popupMenuIndex].section = reqData.updatedCode.section;
          updatedGridData[popupMenuIndex].subSection = reqData.updatedCode.subSection;
          updatedGridData[popupMenuIndex].code = reqData.updatedCode.code;
          updatedGridData[popupMenuIndex].colloquialDescription = reqData.updatedCode.colloquialDescription;
          updatedGridData[popupMenuIndex].shortDescription = reqData.updatedCode.shortDescription;
          updatedGridData[popupMenuIndex].longDescription = reqData.updatedCode.longDescription;
          setGridData(updatedGridData);
          toggleEditDrawer(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  // #endregion

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Edit Drawer */}
      {isEditDrawerOpen
        ? <Drawer
            placement="right"
            visible={isEditDrawerOpen}
            toggleDrawer={() => {
              toggleEditDrawer(false);
              // resetState();
            }}
            zIndex="2000"
            width={window.innerWidth <= 700 ? window.innerWidth : 358}
          >
            <div className={css(styles.drawerContainer)}>
              {/* Header */}
              <div className={css(styles.drawerHeader)}>
                <div className='fx fx__hv-center'>
                  <i className='mr-2 fa fa-lg fa-user' />
                  <div className='noselect'>
                    Edit CPT Code Record
                  </div>
                </div>
                <i
                  className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                  onClick={() => {
                    toggleEditDrawer(false);
                    // resetState();
                  }}
                />
              </div>
              {/* Middle area */}
              <div className={css(styles.drawerContentArea)}>
                  {/* Section */}
                  <div className='mt-2 pl-2'>
                    <TextInputWithLabel
                      value={currentSection}
                      titleText="Section"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={337}
                      showPreIcon={false}
                      onChange={handleOnEditSection}
                    />
                  </div>
                  {/* Sub Section */}
                  <div className='mt-2 pl-2'>
                    <TextInputWithLabel
                      value={currentSubSection}
                      titleText="Sub Section"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={337}
                      showPreIcon={false}
                      onChange={handleOnEditSubSection}
                    />
                  </div>
                  {/* CPT Code */}
                  <div className='mt-2 pl-2'>
                    <TextInputWithLabel
                      value={currentCode}
                      titleText="CPT Code"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={337}
                      showPreIcon={false}
                      onChange={handleOnEditCptCode}
                    />
                  </div>
                  {/* Colloquial Description */}
                  <div className='mt-2 pl-2'>
                    <TextInputWithLabel
                      value={currentColloquialDescription}
                      titleText="Colloquial Description"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={337}
                      showPreIcon={false}
                      onChange={handleOnEditColloquialDescription}
                    />
                  </div>
                  {/* Short Description */}
                  <div className='mt-2 pl-2'>
                    <TextInputWithLabel
                      value={currentShortDescription}
                      titleText="Short Description"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={337}
                      showPreIcon={false}
                      onChange={handleOnEditShortDescription}
                    />
                  </div>
                  {/* Long Description */}
                  <div className='mt-2 pl-2'>
                    <textarea
                      className={css(styles.textArea)}
                      placeholder="Long description"
                      value={currentLongDescription}
                      onChange={handleOnEditLongDescription}
                    />
                  </div>
              </div>
              <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
              {/* Required fields msg */}
              <div className={css(styles.criticalSmallText)} >
                *Required fields
              </div>
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSaveRecord}
                // isDisabled={!canAddUser}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Save Record
              </Button>
            </div>
            </div>
          </Drawer>
        : null
      }
      <div className={css(styles.gridContainer)}>
        {/* Grid toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Grid toolbar - right aligned buttons */}
          <div className='fx'>
            {/* Add user button */}
            {/* <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickToggleUserDrawer}
            >
              <i className="fa fa-lg fa-plus-circle" />
            </div> */}
            {/* Download */}
            {/* <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickDownloadData}
            >
              <i className="fa fa-lg fa-cloud-download" />
            </div> */}
            {/* Trash bin */}
            {/* <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-trash" />
            </div> */}
            {/* Checkbox */}
            {/* <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-check-square" />
            </div> */}
            {/* Reset button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickResetFilters}
            >
              <i className="fa fa-lg fa-refresh" />
            </div>
            {/* Vertical ellipsis */}
            <div
              className={css(styles.gridToolbarIcon)}
              style={{ fontSize: '17px' }}
            >
              <span className='fa-stack'>
                <i className='fa fa-circle fa-stack-2x' />
                <i
                  className='fa fa-ellipsis-v fa-stack-1x'
                  style={{ color: gray[700] }}
                />
                {/* <i class="fa fa-flag fa-stack-1x fa-inverse"></i> */}
              </span>
            </div>
          </div>
        </div>
        {/* Grid ellipsis menu */}
        <div
          ref={gridEllipsisMenu}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenu}
            onOutsideClick={() => { toggleGridEllipsisMenu(false); }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Show details menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickShowDetailsMenuItem}
              >
                Show Details
              </div>
              {/* Delete record menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickDeleteMenuItem}
              >
                Delete Record
              </div>
            </div>
          </PopupMenu>
        </div>
        {/* Data grid */}
        <DataGrid
          loader={showLoader}
          icons={icons}
          headerDefs={gridHeaderDefs}
          rowData={gridData}
          filters={filters}
          gridWidth={gridWidth} // was 550
          gridHeight={gridHeight}
          gridHeaderHeight={48}
          gridFooterHeight={0}
          gridContainerHeight='730px'
          rowHeight={64}
          rowsPerPage={DOCS_PER_PAGE}
          defaultCellStyle={defaultCellStyle}
          showFooter={false}
          showPager
          showEllipsisCol
          enableHover
          totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
          currentPage={currentPage}
          isMobileLayout={isMobileLayout}
          canSelectRows={false}
          onClickCell={() => console.log('Not implemented')}
          onPopupMenu={handleOnPopupMenu}
          onFilterTypeUpdate={handleFilterTypeUpdate}
          onApplyFilter={handleOnApplyFilter}
          onColFilterToggle={handleOnFilterToggle}
          onColPinChange={handleOnPinChange}
          onLoadPage={handleOnLoadPage}
          onSort={handleOnSortOrderChanged}
          onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
          onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
          onResize={() => { console.warn('Handler not set for onResize prop.'); }}
          onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
          onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
          onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
          onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
        />
      </div>
    </div>
  );
};

export default CptDataGrid;
